import { Link } from 'react-router-dom'
import { GlassCard } from '@/components/ui/glass-card'
import { useLuxeHero, useLuxeGrid } from '@gjl/motion/css'
import { Calculator, DollarSign, Percent, TrendingUp, Home, RefreshCw, ArrowLeft } from 'lucide-react'

const calculatorIcons: Record<string, React.ElementType> = {
  'mortgage-payment': DollarSign,
  'cash-on-cash': Percent,
  dscr: TrendingUp,
  'rent-vs-buy': Home,
  brrrr: RefreshCw,
}

export default function CalculatorsPage() {
  const heroRef = useLuxeHero<HTMLDivElement>(0.2)
  const gridRef = useLuxeGrid<HTMLDivElement>('.calc-card')

  const calculators = [
    {
      href: '/resources/calculators/mortgage-payment',
      slug: 'mortgage-payment',
      title: 'Mortgage Payment',
      description:
        'Calculate your monthly principal and interest payments based on loan amount, interest rate, and term.',
    },
    {
      href: '/resources/calculators/cash-on-cash',
      slug: 'cash-on-cash',
      title: 'Cash-on-Cash Return',
      description: 'Measure your annual return on the actual cash you invested in a property.',
    },
    {
      href: '/resources/calculators/dscr',
      slug: 'dscr',
      title: 'DSCR Calculator',
      description: 'Determine if your rental income adequately covers your debt obligations.',
    },
    {
      href: '/resources/calculators/rent-vs-buy',
      slug: 'rent-vs-buy',
      title: 'Rent vs Buy',
      description: 'Compare the long-term costs of renting versus buying a property.',
    },
    {
      href: '/resources/calculators/brrrr',
      slug: 'brrrr',
      title: 'BRRRR Strategy',
      description: 'Model returns using the Buy, Rehab, Rent, Refinance, Repeat strategy.',
    },
  ]

  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="bg-gradient-dark-gold absolute inset-0" />
        <div ref={heroRef} className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <Link
            to="/resources"
            className="text-primary hover:text-primary/80 mb-6 inline-flex items-center gap-2 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Resources
          </Link>
          <div className="bg-primary/10 mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl">
            <Calculator className="text-primary h-8 w-8" />
          </div>
          <h1 className="text-fluid-h1 text-foreground mb-6">Investment Calculators</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto max-w-2xl">
            Professional-grade tools to help you analyze deals and make informed investment decisions.
          </p>
        </div>
      </section>

      {/* Calculators Grid */}
      <section ref={gridRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {calculators.map((calc) => {
              const Icon = calculatorIcons[calc.slug] || Calculator
              return (
                <Link key={calc.href} to={calc.href} className="calc-card group">
                  <GlassCard hover className="h-full">
                    <div className="bg-primary/10 group-hover:bg-primary/20 mb-4 flex h-14 w-14 items-center justify-center rounded-xl transition-colors">
                      <Icon className="text-primary h-7 w-7" />
                    </div>
                    <h3 className="text-foreground group-hover:text-primary mb-3 text-xl font-semibold transition-colors">
                      {calc.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{calc.description}</p>
                  </GlassCard>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* How to Use Section */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <GlassCard accent>
            <h2 className="text-fluid-h3 text-foreground mb-6">How to Use These Calculators</h2>
            <div className="text-muted-foreground space-y-4">
              <p>
                These calculators are designed to give you a quick analysis of potential deals. They use standard
                formulas and assumptions common in real estate investing.
              </p>
              <p>
                <span className="text-foreground font-medium">Important note:</span> These tools provide estimates only.
                Always verify numbers with your lender, accountant, and other professionals before making investment
                decisions.
              </p>
              <p>
                For a comprehensive deal analysis, we recommend using multiple calculators together to understand
                different aspects of a potential investment.
              </p>
            </div>
          </GlassCard>
        </div>
      </section>
    </div>
  )
}
